import { FunctionComponent, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useGetScenarioCostsQuery } from "@/pages/Private/redux/media/media.api";
import { VideoWizard } from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import {
	budgetPercent,
	formatUsd,
	isOverBudgetBand,
	isStepApproved,
	STEP_KEYS,
	stepOf,
} from "./wizard.utils";

interface CostRailProps {
	wizard: VideoWizard;
}

/**
 * What this video has cost and is forecast to cost. Every number is the server's — the
 * per-step actuals, the forecast and the band are all read straight off the aggregate,
 * because a price computed in two places drifts the moment the registry changes.
 */
export const CostRail: FunctionComponent<CostRailProps> = ({ wizard }) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const { data: scenarioCosts, refetch: refetchScenarioCosts } = useGetScenarioCostsQuery(
		wizard.courseId
	);

	// The scenario query is cached independently of the wizard, so it lagged every spend —
	// by the end of a run the total was wrong by orders of magnitude. Refetched whenever
	// this video's own actuals move.
	useEffect(() => {
		refetchScenarioCosts();
	}, [wizard.costs.totalActualUsd, refetchScenarioCosts]);
	const percent = budgetPercent(wizard);
	const overBand = isOverBudgetBand(wizard);
	// What the rest of the scenario cost — phase images and phase TTS — beside this video.
	const otherScenarioCosts = Math.max(
		0,
		(scenarioCosts?.totalUsd ?? 0) - wizard.costs.totalActualUsd
	);

	return (
		<aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[290px]">
			<section className="rounded-card border border-border-soft bg-white p-4">
				<h3 className="mb-3 text-sm font-bold text-ink">{ts("rail.videoCosts")}</h3>

				{STEP_KEYS.map((key, index) => {
					const step = index + 1;
					const state = stepOf(wizard, step);
					const approved = isStepApproved(wizard, step);
					const actualCostUsd = state?.actualCostUsd ?? 0;
					const hasRun = actualCostUsd > 0;

					return (
						<div
							key={key}
							className="flex items-start justify-between gap-2 border-b border-border-soft py-2 last:border-b-0"
						>
							<span className="flex min-w-0 items-start gap-2">
								<span
									className={classNames(
										"mt-1.5 h-2 w-2 shrink-0 rounded-full",
										approved ? "bg-success" : hasRun ? "bg-status-running" : "bg-border"
									)}
								/>
								<span className="min-w-0">
									<span className="block truncate text-xs font-semibold text-text">
										{step} · {ts(`steps.${key}.title`)}
									</span>
									{(state?.generationCount ?? 0) > 0 && (
										<span className="block text-xs text-subtle">
											{ts("rail.generationCount", { times: state?.generationCount ?? 0 })}
										</span>
									)}
								</span>
							</span>
							<span
								className={classNames(
									"shrink-0 text-xs font-bold",
									hasRun ? "text-ink" : "text-subtle"
								)}
							>
								{hasRun ? formatUsd(actualCostUsd) : `~${formatUsd(state?.estimateUsd ?? 0)}`}
							</span>
						</div>
					);
				})}

				<div className="mt-2 flex items-center justify-between border-t border-border pt-2">
					<span className="text-xs font-semibold text-subtle">{ts("rail.actualSoFar")}</span>
					<span className="text-sm font-bold text-ink">
						{formatUsd(wizard.costs.totalActualUsd)}
					</span>
				</div>
				<div className="flex items-center justify-between pt-1">
					<span className="text-xs font-semibold text-subtle">{ts("rail.forecast")}</span>
					<span className="text-xs font-bold text-subtle">
						~{formatUsd(wizard.costs.forecastUsd)}
					</span>
				</div>

				<div className="mt-2.5 h-[7px] overflow-hidden rounded-full bg-surface">
					<div
						className={classNames(
							"h-full rounded-full transition-all",
							overBand ? "bg-danger" : "bg-accent"
						)}
						style={{ width: `${percent}%` }}
					/>
				</div>
				<p className="mt-1.5 text-xs text-subtle">
					{ts("rail.budgetHint", {
						percent,
						min: formatUsd(wizard.costs.budgetBand.minUsd),
						max: formatUsd(wizard.costs.budgetBand.maxUsd),
					})}
				</p>
			</section>

			<section className="rounded-card border border-border-soft bg-white p-4">
				<h3 className="mb-3 text-sm font-bold text-ink">{ts("rail.scenarioCosts")}</h3>
				<div className="flex items-center justify-between border-b border-border-soft py-2">
					<span className="text-xs font-semibold text-text">{ts("rail.thisVideo")}</span>
					<span className="text-xs font-bold text-ink">
						{formatUsd(wizard.costs.totalActualUsd)}
					</span>
				</div>
				<div className="flex items-center justify-between py-2">
					<span className="text-xs font-semibold text-text">{ts("rail.phaseMedia")}</span>
					<span className="text-xs font-bold text-ink">{formatUsd(otherScenarioCosts)}</span>
				</div>
				<div className="flex items-center justify-between border-t border-border pt-2">
					<span className="text-xs font-semibold text-subtle">{ts("rail.scenarioTotal")}</span>
					{/* The sum of the two rows above it, so the panel can never disagree with
					    itself — whatever the cached scenario query still says. */}
					<span className="text-sm font-bold text-ink">
						{formatUsd(wizard.costs.totalActualUsd + otherScenarioCosts)}
					</span>
				</div>
			</section>
		</aside>
	);
};
